import { AppShell, Burger, Group, Title, NavLink, Menu, Button, Text, Anchor } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { Link, useLocation } from 'react-router-dom';
import {
  IconActivity,
  IconLogout,
  IconUser,
  IconMapPin,
  IconCpu,
  IconUsers,
  IconDatabase,
} from '@tabler/icons-react';
import { WebSocketStatus } from './WebSocketStatus';
import { useAuth } from '../contexts/AuthContext';

interface LayoutProps {
  children: React.ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const [opened, { toggle }] = useDisclosure();
  const location = useLocation();
  const { user, signOut } = useAuth();

  const navItems = [
    { label: 'Map', path: '/', icon: IconMapPin },
    { label: 'Personas', path: '/personas', icon: IconUsers },
    { label: 'Detections', path: '/detections', icon: IconDatabase },
    { label: 'Devices', path: '/devices', icon: IconCpu },
  ];

  return (
    <AppShell
      header={{ height: 60 }}
      navbar={{
        width: 250,
        breakpoint: 'sm',
        collapsed: { mobile: !opened },
      }}
      footer={{ height: 40 }}
      padding="md"
    >
      <AppShell.Header>
        <Group h="100%" px="md" justify="space-between">
          <Group>
            <Burger opened={opened} onClick={toggle} hiddenFrom="sm" size="sm" />
            <IconActivity size={24} />
            <Group gap={4} align="baseline">
              <Title order={3}>NHI Dashboard</Title>
              <Text size="xs" c="dimmed">by 33Bit LLC</Text>
            </Group>
          </Group>
          <Group>
            <Anchor href="mailto:info@33bit.com" size="sm" visibleFrom="sm">info@33bit.com</Anchor>
            <WebSocketStatus />
            <Menu shadow="md" width={200}>
              <Menu.Target>
                <Button variant="subtle" leftSection={<IconUser size={16} />}>
                  {user?.email}
                </Button>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Label>Account</Menu.Label>
                <Menu.Item
                  leftSection={<IconLogout size={16} />}
                  onClick={signOut}
                  color="red"
                >
                  Sign Out
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Group>
        </Group>
      </AppShell.Header>

      <AppShell.Navbar p="md">
        {navItems.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.path}
              component={Link}
              to={item.path}
              label={item.label}
              leftSection={<Icon size={20} />}
              active={location.pathname === item.path}
              onClick={() => {
                if (window.innerWidth < 768) toggle();
              }}
            />
          );
        })}
      </AppShell.Navbar>

      <AppShell.Main>{children}</AppShell.Main>

      <AppShell.Footer p="xs">
        <Group justify="space-between" px="md" h="100%">
          <Text size="xs" c="dimmed">© 2025 33Bit Technologies, LLC</Text>
          <Anchor component={Link} to="/terms" size="xs">Terms & Conditions</Anchor>
        </Group>
      </AppShell.Footer>
    </AppShell>
  );
}
